import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { assetUrl, shuffled } from "@/lib/glossyApi";
import MiniAd from "@/components/MiniAd";
import MagazineLayout from "@/components/MagazineLayout";
import { Reveal } from "@/components/Reveal";
import { Tag, CalendarDays, MapPin, Phone, ArrowRight } from "lucide-react";

export default function Noticeboard() {
  const [notices, setNotices] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => { document.title = "The Noticeboard — S/ME"; client.get("/notices").then((r) => setNotices(shuffled(r.data))).catch(() => {}); }, []);
  const shown = filter === "All" ? notices : notices.filter((n) => n.type === filter.toLowerCase());

  return (
    <MagazineLayout>
      <div className="pt-[68px] bg-cream min-h-screen">
        <header className="px-5 sm:px-8 pt-16 sm:pt-24 pb-10 max-w-[1600px] mx-auto">
          <p className="overline text-gloss-silver mb-4">What's On</p>
          <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-none">The Noticeboard</h1>
          <p className="font-sans text-lg text-ink/70 mt-5 max-w-xl">Offers and events from S/ME members across the UK — pinned up for readers who are actually looking.</p>
          <div className="flex gap-3 mt-8">
            {["All", "Offer", "Event"].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`overline px-4 py-2 border transition-colors ${filter === f ? "bg-ink text-cream border-ink" : "border-ink/30 hover:border-ink"}`}
                data-testid={`notice-filter-${f.toLowerCase()}`}>{f}</button>
            ))}
          </div>
        </header>
        <div className="px-5 sm:px-8 max-w-[1600px] mx-auto mb-10"><MiniAd variant="banner" mode="image" space="noticeboard_square" /></div>
        <section className="px-5 sm:px-8 pb-28 max-w-[1600px] mx-auto">
          {shown.length === 0 ? (
            <p className="font-sans text-ink/50 py-20 text-center">Nothing pinned up yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 border-t border-ink/20 pt-12" data-testid="notice-grid">
              {shown.map((n, i) => (
                <Reveal key={n.id} delay={(i % 3) * 0.06}>
                  <div className="border border-ink/20 h-full flex flex-col bg-cream hover:shadow-xl hover:-translate-y-0.5 transition-all overflow-hidden" data-testid="notice-card">
                    <NoticePoster n={n} />
                    <div className="p-7 flex flex-col flex-1">
                      <div className="flex items-center justify-between gap-2 mb-4">
                        <div className="flex items-center gap-2">
                          {n.type === "event" ? <CalendarDays size={15} className="text-gloss-silver" /> : <Tag size={15} className="text-gloss-silver" />}
                          <span className="overline text-gloss-silver">{n.type}</span>
                        </div>
                      </div>
                      <h3 className="font-serif text-2xl sm:text-3xl tracking-tight leading-tight">{n.title}</h3>
                      <p className="font-sans text-base text-ink/70 mt-3 leading-relaxed flex-1">{n.body}</p>
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-ink/15">
                        <span className="font-serif text-lg">{n.member_name}</span>
                        {n.borough && <span className="flex items-center gap-1 font-mono text-xs uppercase tracking-wider text-ink/50"><MapPin size={12} /> {n.borough}</span>}
                      </div>
                      {(n.contact?.website || n.contact?.phone || n.member_id) && (
                        <div className="flex flex-wrap items-center gap-2 mt-4" data-testid={`notice-contact-${n.id}`}>
                          {n.contact?.website && (
                            <a href={n.contact.website.startsWith("http") ? n.contact.website : `https://${n.contact.website}`} target="_blank" rel="noreferrer"
                              className="overline text-[10px] text-cream px-4 py-2.5 inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity" style={{ backgroundColor: n.accent || "#141414" }} data-testid={`notice-cta-${n.id}`}>
                              {n.type === "event" ? "Book now" : "Claim this offer"} <ArrowRight size={12} />
                            </a>
                          )}
                          {n.contact?.phone && (
                            <a href={`tel:${n.contact.phone.replace(/\s/g, "")}`} className="overline text-[10px] border border-ink/30 px-3 py-2.5 inline-flex items-center gap-1.5 hover:border-ink transition-colors" data-testid={`notice-phone-${n.id}`}>
                              <Phone size={11} /> {n.contact.phone}
                            </a>
                          )}
                          {n.member_id && (
                            <Link to={`/member/${n.member_id}`} className="overline text-[10px] text-ink/50 hover:text-ink transition-colors ml-auto" data-testid={`notice-profile-${n.id}`}>Profile →</Link>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
          <div className="mt-14 bg-ink text-cream p-8 grain">
            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="font-serif text-2xl sm:text-3xl max-w-xl">Want your offer up here? It's part of membership.</p>
              <Link to="/join" className="bg-cream text-ink px-7 py-4 overline hover:bg-gloss-silver transition-colors shrink-0" data-testid="noticeboard-join">Become a member</Link>
            </div>
          </div>
        </section>
      </div>
    </MagazineLayout>
  );
}

// Brand-coloured DESIGNED poster (default) with a photo option — proper poster art, 3 layouts.
function NoticePoster({ n }) {
  const accent = n.accent || "#141414";
  const logo = assetUrl(n.logo);
  const isEvent = n.type === "event";
  const v = (n.poster_variant ?? 0) % 3;
  const initial = (n.member_name || "S")[0];

  if (n.use_photo && assetUrl(n.poster_image)) {
    return (
      <div className="relative aspect-[3/2] overflow-hidden bg-ink" data-testid="notice-poster">
        <img src={assetUrl(n.poster_image)} alt={n.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />
        <div className="absolute inset-0 flex flex-col p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <span className="font-serif text-cream text-xl tracking-tight">S/ME</span>
            {logo ? <span className="bg-cream/95 px-2 py-1"><img src={logo} alt={n.member_name} className="h-4 w-auto object-contain" data-testid="notice-logo" /></span>
              : <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-cream/80">{isEvent ? "Event" : "Offer"}</span>}
          </div>
          <div className="mt-auto">
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-gloss-gold">{isEvent ? "What's On" : "Reader Offer"}</span>
            <p className="font-serif text-cream leading-[0.95] tracking-tight text-2xl sm:text-[2rem] mt-1.5">{n.title}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/70 mt-2">{n.member_name}{n.borough ? ` · ${n.borough}` : ""}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative aspect-[3/2] overflow-hidden grain" style={{ backgroundColor: accent }} data-testid="notice-poster">
      {/* graphic layers */}
      <div className="absolute inset-0 z-0 opacity-[0.16]" style={{
        backgroundImage: v === 1
          ? "repeating-linear-gradient(45deg, rgba(255,255,255,0.7) 0 1px, transparent 1px 18px)"
          : v === 2
          ? "radial-gradient(circle at 85% 20%, rgba(255,255,255,0.9) 0, rgba(255,255,255,0) 45%)"
          : "none",
      }} />
      {/* oversized translucent initial for depth */}
      <span className="absolute -bottom-10 -right-3 z-0 font-serif leading-none select-none text-cream/10" style={{ fontSize: "13rem" }}>{initial}</span>

      {/* print-poster frame */}
      <div className="absolute inset-3 sm:inset-4 border border-cream/25 z-10 pointer-events-none" />

      <div className="absolute inset-0 z-20 flex flex-col p-5 sm:p-6">
        {/* masthead row */}
        <div className="flex items-center justify-between">
          <span className="font-serif text-cream text-xl tracking-tight">S/ME</span>
          {logo ? (
            <span className="bg-cream/95 px-2 py-1"><img src={logo} alt={n.member_name} className="h-4 w-auto object-contain" data-testid="notice-logo" /></span>
          ) : (
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-cream/70">{isEvent ? "Event" : "Offer"}</span>
          )}
        </div>

        {/* hero type */}
        <div className="flex-1 flex flex-col justify-center">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-gloss-gold mb-2">{isEvent ? "What's On" : "Reader Offer"}</span>
          <p className="font-serif text-cream leading-[0.92] tracking-tight text-3xl sm:text-[2.5rem]">{n.title}</p>
        </div>

        {/* footer credit */}
        <div className="flex items-center justify-between border-t border-cream/25 pt-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/80">{n.member_name}</span>
          {n.borough && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60">{n.borough}</span>}
        </div>
      </div>
    </div>
  );
}
